// BI.1.2 Add Linked Account
import moment from 'moment';
import { select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import { createAccount, linkedAccountsApi, removeErrors, showSuccessErrorMessage, getErrorMessages } from './api';
import multistepMixin from '../shared/vue/mixins/multistep';
import { removeUnavailableError } from '../shared/api';
import accountLookup from '../shared/vue/mixins/accountLookup';

/**
 * Helper function to clear the fields and hide the verify fields
 * @param {object} model The Vue instance
 */
function clearVerifyFields(model) {
  /* eslint-disable no-param-reassign */
  model.modelDateOfBirth = null;
  model.modelLast4Ssn = null;
  model.modelTaxId = null;
  model.showVerifyFields = false;
  removeErrors();
  /* eslint-enable */
}

/**
 * Enrolling in auto pay
 */
export default function addLinkedAccount() {
  const el = select('[data-vue-app="add-linked-account"]');
  if (el) {
    const app = new Vue({ // eslint-disable-line no-unused-vars
      el,
      delimiters,
      mixins: [multistepMixin, accountLookup],
      data: {
        // for accessibility in live regions
        liveText: '',
        agree: false,
        date: '',
        modelLdc: 'KY',
        // the account number value
        modelAccountNumber: null,
        // the phone number value
        modelPhone: null,
        modelDateOfBirth: null,
        modelLast4Ssn: null,
        modelTaxId: null,
        modelPin: null,
        // The service addresses (will be populated via API response)
        modelServiceAddresses: [],
        // list of customer account IDs the user has selected
        linkedAccounts: [],
        selectedCustomerAccounts: [],
        // check if 'Select All' checkbox is checked
        allAccountsSelected: false,
        modelNickname: null,
        // secondary lookup field ID
        secondaryLookupFieldName: '',
        // whether to show the verify fields
        showVerifyFields: false,
        // whether to show alternative lookup fields
        showAlternateLookup: false,
        // the confirm button disabled by default
        showConfirmAccountButton: true,
        // the confirm button disabled by default
        enableConfirmAccountButton: false,
        // the submit button disabled by default
        showSubmitAccountButton: false,
        // the search by phone button should be disabled by default
        disabledSearchByPhone: true,
        // the search by account button should be disabled by default
        disabledAccountNumber: true,
        // disable verification field
        disableVerificationField: false,
        success: false,
        errorMessage: null,
        noAccountsFound: false,
        accountNotAdded: false,
        isReady: false,
        errorMessages: getErrorMessages(), // JSON.parse(document.getElementById('errors').innerHTML),
        enabledLdcs: null,
        nonDeployedLdc: '',
      },

      mounted() {
        // Find the LDC for which the account should be created.
        // This will be added to the DOM via back-end.
        if (this.$el.dataset.ldc) {
          this.modelLdc = this.$el.dataset.ldc;
          // now we're ready for the app to load
          this.isReady = true;
        }
        // once app is mounted, check for deployed LDCs
        if (this.$el.dataset.enabledLdcs) {
          this.enabledLdcs = this.$el.dataset.enabledLdcs;
        }
      },

      created() {
        Event.$on('UpdateNoAccountsFoundFlag', (response) => {
          if (response) {
            this.noAccountsFound = response.noAccountsFound;
            removeErrors();
          }
        });
      },

      methods: {
        /**
         * Handle the JSON response of the lookup for getting secondary lookup field
         * @this Vue The Vue app instance
         * @param {object} response
         * @param {string} response.hasDateOfBirth
         * @param {string} response.hasTaxId
         * @param {string} response.hasLastFourSocial
         */
        handleGetSecondaryLookupFieldResponse(response) {
          if (response.dateOfBirthCount > 0 || response.lastFourSocialCount > 0 || response.taxIdCount > 0 || response.pinCount > 0) {
            this.modelDateOfBirth = Array(response.dateOfBirthCount || 0).map(() => '');
            this.modelLast4Ssn = Array(response.lastFourSocialCount || 0).map(() => '');
            this.modelTaxId = Array(response.taxIdCount || 0).map(() => '');
            this.modelPin = Array(response.pinCount || 0).map(() => '');
            this.showVerifyFields = true;
            this.disabledAccountNumber = true;
            Vue.nextTick(() => {
              document.querySelector('.create-account__verify input').focus();
            });
          } else {
            showSuccessErrorMessage(this.errorMessages, 'RegisterAccount-NoSecondaryInfoAvailable');
          }
        },

        /**
         * Click on the confirm secondary validation
         */
        clickConfirmAccount() {
          this.loading = true;

          if (window.fabricator) {
            setTimeout(() => {
              this.disableVerificationField = true;
              this.modelServiceAddresses = [
                {
                  customerAccountId: '123456789',
                  addresses: [
                    {
                      address1: '274 Marconi Blvd',
                      address2: 'Suite 300',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43215,
                    },
                  ],
                  valid: false,
                  alreadyLinked: true,
                },
                {
                  customerAccountId: '123456780',
                  addresses: [
                    {
                      address1: '274 Marconi Blvd',
                      address2: 'Suite 300',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43215,
                    },
                    {
                      address1: '200 S Civic Center Drive',
                      address2: '',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43215,
                    },
                  ],
                  valid: true,
                  alreadyLinked: true,
                },
                {
                  customerAccountId: '123456781',
                  addresses: [
                    {
                      address1: '100 Northbend Drive',
                      address2: '',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43216,
                    },
                    {
                      address1: '55 E River Avenue',
                      address2: '',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43207,
                    },
                  ],
                  valid: true,
                  alreadyLinked: false,
                },
              ];
              this.linkedAccounts = this.modelServiceAddresses.filter(account => account.alreadyLinked);
              this.modelServiceAddresses = this.modelServiceAddresses.filter(account => !account.alreadyLinked);
              if (this.modelServiceAddresses.length === 1 && this.linkedAccounts.length === 0) {
                this.selectAllAddressesByDefault();
              }
              this.loading = false;
            }, 500);
          } else {
            // prepare the API info
            const params = this.createLookupParams();

            // do the api lookup
            createAccount.verifyAccountOwnership(params).then((response) => {
              this.loading = false;
              if (response.data) {
                if (response.data.notValidatedCount > 0) {
                  // Not valid second authentication
                  showSuccessErrorMessage(this.errorMessages, 'RegisterAccount-VerifyAccountInvalid');
                } else if (response.data.entries) {
                  this.disableVerificationField = true;
                  this.linkedAccounts = response.data.entries.filter(account => account.alreadyLinked);
                  this.modelServiceAddresses = response.data.entries.filter(account => !account.alreadyLinked);
                  // WEBPAY-638: if there is exactly one account, select it by default
                  if (this.modelServiceAddresses.length === 1 && this.linkedAccounts.length === 0) {
                    this.selectAllAddressesByDefault();
                  }
                  this.showConfirmAccountButton = false;
                  this.showSubmitAccountButton = true;
                } else {
                  // No entries?
                  showSuccessErrorMessage(this.errorMessages, 'RegisterAccount-NoSecondaryInfoAvailable-FailAttempts');
                }
              }
            })
              .catch(() => {
                this.loading = false;
              });
          }
        },

        /**
         * Submit new account to back-end and link
         */
        submitAccount() {
          this.loading = true;
          const accountId = this.selectedCustomerAccounts[0];

          const requestData = {
            dateOfBirth: this.formattedDatesOfBirth,
            last4Ssn: this.modelLast4Ssn.length > 0 ? this.modelLast4Ssn.filter(ssn => ssn) : [],
            taxId: this.modelTaxId.length > 0 ? this.modelTaxId.map(tid => tid.replace(/-/g, '')) : [],
            pins: this.modelPin.length > 0 ? this.modelPin.filter(pin => pin) : [],
            registrations: this.buildRegistrationDataList(),
          };

          if (window.fabricator) {
            setTimeout(() => {
              this.disableVerificationField = true;
              this.modelServiceAddresses = [
                {
                  customerAccountId: '123456789',
                  addresses: [
                    {
                      address1: '274 Marconi Blvd',
                      address2: 'Suite 300',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43215,
                    },
                    {
                      address1: '1234 State St',
                      address2: '',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43215,
                    },
                  ],
                  valid: false,
                  alreadyLinked: true,
                },
                {
                  customerAccountId: '123456780',
                  addresses: [
                    {
                      address1: '200 S Civic Center Drive',
                      address2: '',
                      city: 'Columbus',
                      state: 'OH',
                      zip: 43215,
                    },
                  ],
                  valid: true,
                  alreadyLinked: false,
                },
              ];
              this.loading = false;
            }, 500);
          } else {
            linkedAccountsApi.linkNewAccount(requestData)
              .then(() => {
                location.href = `/account/manage/${accountId.replace(/-/g, "")}?added=true`;
              })
              .catch(() => {
                this.accountNotAdded = true;
                this.loading = false;
                removeErrors();
              });
          }
        },

        buildRegistrationDataList() {
          return this.selectedCustomerAccounts.map(accountId => {
            const validationEntries = this.modelServiceAddresses.filter(v => v.customerAccountId === accountId);
            return {
              customerAccountId: accountId,
              ldc: validationEntries[0].ldc,
              nickname: 'My Account',
            };
          });
        },

        /**
         * Create parameter for address lookup
         */
        createLookupParams() {
          const params = {
            // use date of birth because we have a value
            accountId: this.modelAccountNumber,
            phone: this.unmaskedPhoneNumber, // this.modelPhone,
            secondarySearchValues: [],
          };

          // populate the verify criteria
          if (this.modelDateOfBirth.length > 0) {
            this.modelDateOfBirth.forEach(dob => {
              if (dob) {
                params.secondarySearchValues.push({
                  secondaryValue: dob.replace(/ /g, ""),
                  secondarySearchValueType: "DATE_OF_BIRTH",
                });
              }
            });
          }
          if (this.modelLast4Ssn.length > 0) {
            this.modelLast4Ssn.forEach(ssn => {
              if (ssn) {
                params.secondarySearchValues.push({
                  secondaryValue: ssn,
                  secondarySearchValueType: "LAST_4_SOCIAL",
                });
              }
            });
          }
          if (this.modelTaxId.length > 0) {
            this.modelTaxId.forEach(tid => {
              if (tid) {
                params.secondarySearchValues.push({
                  secondaryValue: tid.replace(/-/g, ""),
                  secondarySearchValueType: "TAX_ID",
                });
              }
            });
          }
          if (this.modelPin.length > 0) {
            this.modelPin.forEach(pin => {
              if (pin) {
                params.secondarySearchValues.push({
                  // WEBPAY-1921
                  secondaryValue: this.modelLdc === 'BS' || this.modelLdc === 'NI' ? pin : pin.toUpperCase(),
                  secondarySearchValueType: "PIN",
                });
              }
            });
          }

          if (this.modelLdc) {
            params.ldc = this.modelLdc.toUpperCase();
          }

          return params;
        },

        selectAllAddressesByDefault() {
          this.selectedCustomerAccounts = this.modelServiceAddresses.map(e => e.customerAccountId);
          this.allAccountsSelected = true;
        },

        updateSelectAllAccounts() {
          if (this.selectedCustomerAccounts.length === this.modelServiceAddresses.length) {
            this.allAccountsSelected = true;
          } else {
            this.allAccountsSelected = false;
          }
        },

        selectAllAccounts() {
          this.allAccountsSelected = !this.allAccountsSelected;
          this.selectedCustomerAccounts = [];

          if (this.allAccountsSelected) {
            this.selectedCustomerAccounts = this.modelServiceAddresses.map(e => e.customerAccountId);
          }
        },

        /**
         * Keyup on any Verify field
         */
        keyupOnVerifyField() {
          let dobPass = true;
          let ssnPass = true;
          let taxPass = true;
          let pinPass = true;
          if (this.secondaryLookupFieldName === 'dob') {
            dobPass = this.modelDateOfBirth !== null;
          }
          if (this.secondaryLookupFieldName === 'tax') {
            taxPass = this.modelTaxId !== null;
          }
          if (this.secondaryLookupFieldName === 'ssn') {
            ssnPass = this.modelLast4Ssn !== null;
          }
          if (this.secondaryLookupFieldName === 'pin') {
            pinPass = this.modelPin !== null;
          }

          this.enableConfirmAccountButton = dobPass && ssnPass && taxPass && pinPass;
        },

        resetSearch() {
          clearVerifyFields(this);
          this.modelAccountNumber = null;
          this.modelPhone = null;
          this.disabledAccountNumber = true;
          this.disabledSearchByPhone = true;
          this.showAlternateLookup = false;
          this.showVerifyFields = false;
          this.secondaryLookupFieldName = '';
          this.modelServiceAddresses = [];
          this.showConfirmAccountButton = true;
          this.showSubmitAccountButton = false;
          this.disableVerificationField = false;
          this.noAccountsFound = false;
          this.accountNotAdded = false;
          this.selectedCustomerAccounts = [];
          this.allAccountsSelected = false;
          this.linkedAccounts = [];
          removeUnavailableError();
          setTimeout(() => {
            document.querySelector('#account-number-input').focus();
          }, 0);
        },
      },

      computed: {
        accountNumberPlaceholder() {
          // The placeholder for the account number changes based on LDC.
          if (this.modelLdc === 'NI' || this.modelLdc === 'BS') {
            return 'XXX - XXX - XXX - X';
          }
          return 'XXXXXXXX - XXX - XXXX';
        },
        // keeping "unmasking" here in case the user adds dashes themselves
        unmaskedAccountNumber() {
          return this.modelAccountNumber ? this.modelAccountNumber.replace(/-/g, "") : '';
        },
        unmaskedPhoneNumber() {
          return this.modelPhone ? this.modelPhone.replace(/\D/g, "") : "";
        },
        enableSubmitAccountButton() {
          if (!this.selectedCustomerAccounts && this.selectedCustomerAccounts.length === 1) {
            return true;
          }
          return false;
        },
        formattedDatesOfBirth() {
          return this.modelDateOfBirth.length > 0 ? this.modelDateOfBirth.filter(dob => dob).map(d => moment(d, 'MM/DD/YYYY').format('YYYY-MM-DD[T]HH:mm:ss[Z]')) : [];
        },
        wssLink() {
          return `<a href="/wss/${this.nonDeployedLdc}">the old system</a>`;
        },
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/addLinkedAccount.js